import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2, Circle, Bell, BellOff, RadioTower } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ReviewForm from "@/components/ReviewForm";

const STATUSES = ["received", "confirmed", "preparing", "collecting", "on-the-way", "delivered"];

const OrderTracking = () => {
  const { orderNumber } = useParams();
  const { t } = useLang();
  const [order, setOrder] = useState(null);
  const [notifSupported] = useState(() => typeof window !== "undefined" && "Notification" in window);
  const [notifPerm, setNotifPerm] = useState(() =>
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );
  const prevStatusRef = useRef(null);

  const labels = {
    received: t("orderReceived"),
    confirmed: t("restaurantConfirmed"),
    preparing: t("beingPrepared"),
    collecting: t("driverCollecting"),
    "on-the-way": t("onTheWay"),
    delivered: t("delivered"),
  };

  const askPermission = async () => {
    if (!notifSupported) return;
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
      if (p === "granted") toast.success("Push notifications enabled");
    } catch {}
  };

  const fireNotification = (status) => {
    if (!notifSupported || Notification.permission !== "granted") return;
    try {
      new Notification("Caleta Eats — order update", {
        body: `#${orderNumber}: ${labels[status] || status}`,
        icon: "/favicon.ico",
        tag: `order-${orderNumber}`,
      });
    } catch {}
    // A subtle chime via Web Audio
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } catch {}
  };

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get(`/orders/${orderNumber}`)
        .then((r) => {
          if (!alive) return;
          const data = r.data;
          setOrder(data);
          if (prevStatusRef.current && prevStatusRef.current !== data.status) {
            toast.success(`Status: ${labels[data.status] || data.status}`, { duration: 6000 });
            fireNotification(data.status);
          }
          prevStatusRef.current = data.status;
        })
        .catch(() => {});
    load();
    const iv = setInterval(load, 5000);
    return () => { alive = false; clearInterval(iv); };
    // eslint-disable-next-line
  }, [orderNumber]);

  if (!order) return <div className="min-h-[50vh] flex items-center justify-center text-navy/50">Loading…</div>;

  const currentIdx = STATUSES.indexOf(order.status);

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-navy/50">Order #{order.order_number}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-navy tracking-tight mt-1">{t("yourOrder")}</h1>
          <p className="text-navy/60 mt-1">{order.restaurant_name}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-teal font-semibold bg-teal/10 px-3 py-1.5 rounded-full">
          <RadioTower className="w-3 h-3 animate-pulse" /> Live
        </div>
      </div>

      {notifSupported && notifPerm !== "granted" && notifPerm !== "denied" && (
        <div className="mt-6 rounded-2xl border border-coral/40 bg-coral/5 p-4 flex items-center gap-3 flex-wrap">
          <Bell className="w-5 h-5 text-coral" />
          <span className="text-sm text-navy flex-1 min-w-[200px]">Get a push notification every time your order moves forward.</span>
          <Button onClick={askPermission} className="btn-coral h-9 px-4 text-xs" data-testid="enable-push-btn">Enable</Button>
        </div>
      )}
      {notifPerm === "denied" && (
        <div className="mt-6 rounded-2xl border border-navy/10 bg-sand-50 p-3 flex items-center gap-2 text-xs text-navy/60">
          <BellOff className="w-4 h-4" /> Push notifications blocked in your browser — updates still appear here in real time.
        </div>
      )}

      <div className="mt-8 bg-white rounded-2xl border border-navy/10 p-6 space-y-4">
        {STATUSES.map((s, i) => {
          const done = i <= currentIdx;
          const current = i === currentIdx;
          return (
            <div key={s} className={`flex items-center gap-3 ${done ? "" : "opacity-40"}`} data-testid={`status-${s}`}>
              {done ? <CheckCircle2 className={`w-6 h-6 ${current ? "text-coral" : "text-teal"} ${current ? "animate-pulse" : ""}`} /> : <Circle className="w-6 h-6 text-navy/30" />}
              <span className={`font-medium ${current ? "text-coral" : "text-navy"}`}>{labels[s]}</span>
              {current && <span className="ml-auto text-[10px] uppercase font-bold text-white bg-coral px-2 py-0.5 rounded-full">Now</span>}
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-sand-50 rounded-2xl p-5 text-center">
        <div className="text-xs uppercase tracking-wider text-navy/50">{t("estimatedArrival")}</div>
        <div className="font-heading text-2xl font-bold text-navy mt-1">
          {order.delivery_time === "ASAP" ? "20-40 min" : order.delivery_time}
        </div>
      </div>

      {order.status === "delivered" && (
        <ReviewForm orderNumber={order.order_number} restaurantName={order.restaurant_name} />
      )}

      <a href="mailto:hello@caletaeats.demo"><Button variant="outline" className="mt-6 w-full rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white" data-testid="contact-support-btn">{t("contactSupport")}</Button></a>
    </div>
  );
};

export default OrderTracking;
